import React from 'react';
import { Icon } from '../icons.jsx';
import {
  LizziLogo, Avatar, AvatarStack, StatusChip,
  Sidebar, UserMenu, MenuItem,
  Topbar, CommandPalette,
  ChatDock, ChatMessage, FormattedText, ChatBlock, bidTitle,
  ToastHost,
  ProgressBar, StatPill,
  fmtMoney, fmtPct, fmtDate, fmtDateFull,
} from '../components.jsx';
import { NewBidModal, UploadModal, NotificationsPanel, notify } from '../modals.jsx';
import { MockData } from '../mock/index.js';

const { useState, useEffect, useRef, useMemo, useCallback } = React;

// ===========================
// Dashboard — data-driven (real) + estados vacíos
// ===========================
function DashboardScreen({ setRoute, openBid }) {
  const { BIDS = [], CALENDAR = [], USERS = [], CURRENT_USER = {}, STATUS_LABELS = {} } = window.MockData || {};
  const active = BIDS.filter(b => ['spotted','analysis','preparation','solvency'].includes(b.status));
  const featured = BIDS.find(b => b.flagship) || active[0] || null;
  const hour = new Date().getHours();
  const greet = hour < 12 ? 'Buenos días' : hour < 20 ? 'Buenas tardes' : 'Buenas noches';
  const firstName = (CURRENT_USER.name || 'Usuario').split(' ')[0];

  // KPIs reales
  const pipelineValue = active.reduce((s, b) => s + (b.budgetBase || 0), 0);
  const upcoming = CALENDAR
    .filter(e => e.date && new Date(e.date) >= new Date(new Date().toDateString()))
    .sort((a, b) => new Date(a.date) - new Date(b.date));
  const nextDeadlineBid = BIDS
    .filter(b => (b.deadlineDays ?? -1) > 0)
    .sort((a, b) => a.deadlineDays - b.deadlineDays)[0];

  const wonCount = BIDS.filter(b => b.status === 'won').length;
  const lostCount = BIDS.filter(b => ['lost','discarded'].includes(b.status)).length;
  const resolved = wonCount + lostCount;
  const successRate = resolved ? Math.round((wonCount / resolved) * 100) : null;

  const pipelineSegments = [
    { l:'Avistada',    status:'spotted',     c:'sem-info' },
    { l:'Análisis',    status:'analysis',    c:'sem-violet' },
    { l:'Preparación', status:'preparation', c:'sem-warning' },
    { l:'Presentada',  status:'submitted',   c:'sem-neutral' },
    { l:'Adjudicadas', status:'won',         c:'sem-success' },
    { l:'Perdidas',    status:'lost',        c:'sem-danger' },
  ].map(s => ({ ...s, n: BIDS.filter(b => b.status === s.status).length }));


  // Estado vacío: app real recién empezada
  const isEmpty = BIDS.length === 0;

  return (
    <div className="viewport-inner fade-in">
      {/* Hero */}
      <div className="page-head">
        <div style={{flex:1}}>
          <div className="page-eyebrow">{new Date().toLocaleDateString('es-ES', { weekday:'long', day:'numeric', month:'long' }).toUpperCase()}</div>
          <h1 className="page-title">{greet}, {firstName}.</h1>
          <p style={{fontSize:15, color:'var(--text-2)', maxWidth:580, marginTop:6, lineHeight:1.5}}>
            {isEmpty
              ? <>Tu espacio está listo. Crea tu primera licitación para empezar a trabajar con Lizzi.</>
              : <>Tienes <strong style={{color:'var(--text-1)'}}>{active.length} {active.length === 1 ? 'propuesta activa' : 'propuestas activas'}</strong>{nextDeadlineBid ? <> y el próximo cierre es <strong style={{color:'var(--primary)'}}>{nextDeadlineBid.entityShort || bidTitle(nextDeadlineBid)}</strong> en {nextDeadlineBid.deadlineDays} días.</> : '.'}</>}
          </p>
        </div>
        <div style={{display:'flex', gap:8}}>
          <button className="btn btn-outline" onClick={()=>window.dispatchEvent(new CustomEvent('lizzi:open-upload'))}><Icon.Upload size={14}/> Subir pliego</button>
          <button className="btn btn-primary" onClick={()=>window.dispatchEvent(new CustomEvent('lizzi:open-new-bid'))}><Icon.Plus size={14}/> Nueva licitación</button>
        </div>
      </div>

      {isEmpty ? (
        <div className="card card-pad" style={{padding:60, textAlign:'center', display:'flex', flexDirection:'column', alignItems:'center', gap:14}}>
          <div style={{width:56, height:56, borderRadius:16, background:'var(--bg-2)', display:'flex', alignItems:'center', justifyContent:'center'}}>
            <Icon.Briefcase size={26} style={{color:'var(--text-3)'}}/>
          </div>
          <div className="font-display-heavy" style={{fontSize:22}}>Aún no hay licitaciones</div>
          <p style={{fontSize:14, color:'var(--text-3)', maxWidth:440}}>
            Empieza creando una licitación o subiendo un pliego. Todo lo que crees se guardará en tu espacio y aparecerá aquí.
          </p>
          <div style={{display:'flex', gap:8, marginTop:6}}>
            <button className="btn btn-outline" onClick={()=>window.dispatchEvent(new CustomEvent('lizzi:open-upload'))}><Icon.Upload size={14}/> Subir pliego</button>
            <button className="btn btn-primary" onClick={()=>window.dispatchEvent(new CustomEvent('lizzi:open-new-bid'))}><Icon.Plus size={14}/> Nueva licitación</button>
          </div>
        </div>
      ) : (
        <>
          {featured && <FeaturedBid bid={featured} onOpen={()=>openBid(featured.id)}/>}

          {/* KPI strip */}
          <div style={{display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:18, margin:'28px 0'}}>
            <KpiCardV2
              label="Pipeline activo"
              value={fmtMoney(pipelineValue)}
              sub={`${active.length} ${active.length === 1 ? 'propuesta' : 'propuestas'} · ${USERS.length} ${USERS.length === 1 ? 'persona' : 'personas'}`}
              icon="Briefcase"
            />
            <KpiCardV2
              label="Tasa de éxito"
              value={successRate == null ? '—' : String(successRate)}
              unit={successRate == null ? '' : '%'}
              sub={resolved ? `${wonCount} ganadas · ${lostCount} cerradas` : 'Sin resultados aún'}
              tone="accent"
              icon="Target"
            />
            <KpiCardV2
              label="Próximo plazo"
              value={nextDeadlineBid ? String(nextDeadlineBid.deadlineDays) : '—'}
              unit={nextDeadlineBid ? 'días' : ''}
              sub={nextDeadlineBid ? `${nextDeadlineBid.entityShort || nextDeadlineBid.title}` : 'Sin plazos próximos'}
              tone="warning"
              icon="Clock"
            />
          </div>

          {/* Two column main */}
          <div style={{display:'grid', gridTemplateColumns:'minmax(0, 1.8fr) minmax(0, 1fr)', gap:18}}>
            <div style={{display:'flex', flexDirection:'column', gap:18, minWidth:0}}>
              <div className="card">
                <SectionHeader title="Propuestas activas" sub="En análisis, redacción o revisión" action="Ver todas" onAction={()=>setRoute('bids')}/>
                <div>
                  {active.length === 0 && (
                    <div style={{padding:40, textAlign:'center', color:'var(--text-3)', fontSize:13}}>No hay propuestas activas ahora mismo.</div>
                  )}
                  {active.map((b,i) => (
                    <ActiveBidRow key={b.id} bid={b} onOpen={()=>openBid(b.id)} last={i===active.length-1}/>
                  ))}
                </div>
              </div>

              <div className="card card-pad">
                <SectionHeader title="Estado del pipeline" sub={`Tu portfolio completo de ${BIDS.length} ${BIDS.length === 1 ? 'licitación' : 'licitaciones'}`} plain/>
                <div style={{marginTop:18}}>
                  <PipelineBar segments={pipelineSegments}/>
                  <div style={{display:'grid', gridTemplateColumns:'repeat(6,1fr)', gap:10, marginTop:18}}>
                    {pipelineSegments.map((b,i)=> (
                      <div key={i} style={{display:'flex', flexDirection:'column', gap:4}}>
                        <span style={{fontSize:11, color:'var(--text-3)', textTransform:'uppercase', letterSpacing:'0.06em'}}>{b.l}</span>
                        <div style={{display:'flex', alignItems:'baseline', gap:6}}>
                          <span className="font-display-heavy" style={{fontSize:24}}>{b.n}</span>
                          <span className={`sem ${b.c}`} style={{padding:'2px 6px', fontSize:9}}><span className="chip-dot" style={{background:'currentColor'}}></span></span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Right column */}
            <div style={{display:'flex', flexDirection:'column', gap:18, minWidth:0}}>
              <div className="card-accent" style={{padding:24, position:'relative'}}>
                <div className="glow-orb" style={{width:240, height:240, background:'var(--lime)', top:-100, right:-80, opacity:0.45}}></div>
                <div style={{position:'relative', zIndex:1}}>
                  <div style={{display:'flex', alignItems:'center', gap:10, marginBottom:14}}>
                    <div style={{width:36, height:36, borderRadius:10, background:'rgba(255,255,255,0.18)', display:'flex', alignItems:'center', justifyContent:'center', backdropFilter:'blur(8px)'}}>
                      <Icon.Sparkles size={17}/>
                    </div>
                    <span style={{fontSize:11, color:'rgba(255,255,255,0.72)', letterSpacing:'0.10em', textTransform:'uppercase', fontWeight:600}}>Chat con Lizzi</span>
                  </div>
                  <div className="font-display-heavy" style={{fontSize:24, lineHeight:1.15, letterSpacing:'-0.02em', color:'#fff'}}>
                    Pregúntale<br/>lo que sea.
                  </div>
                  <div style={{fontSize:13, color:'rgba(255,255,255,0.78)', marginTop:10, lineHeight:1.5}}>
                    Conoce tus pliegos, el tarifario y las memorias previas. Disponible con <span className="kbd" style={{background:'rgba(0,0,0,0.25)', color:'#fff', borderColor:'rgba(255,255,255,0.20)'}}>⌘</span> <span className="kbd" style={{background:'rgba(0,0,0,0.25)', color:'#fff', borderColor:'rgba(255,255,255,0.20)'}}>J</span>.
                  </div>
                  <div style={{display:'flex', gap:6, marginTop:16, flexWrap:'wrap'}}>
                    {['Resumir un pliego', 'Simular oferta'].map(s=>(
                      <button key={s} className="chip" onClick={()=>window.dispatchEvent(new CustomEvent('lizzi:open-chat'))}
                        style={{background:'rgba(255,255,255,0.14)', borderColor:'rgba(255,255,255,0.20)', color:'#fff', cursor:'pointer'}}>
                        <Icon.Spark size={11}/> {s}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Next milestones */}
              <div className="card">
                <SectionHeader title="Próximos hitos" sub="Calendario"/>
                <div style={{padding:'4px 18px 18px'}}>
                  {upcoming.length === 0 && (
                    <div style={{padding:'20px 4px', textAlign:'center', color:'var(--text-3)', fontSize:13}}>Sin hitos próximos.</div>
                  )}
                  {upcoming.slice(0,4).map((e) => (
                    <CalendarRow key={e.id} entry={e}/>
                  ))}
                  <button className="btn btn-ghost" style={{fontSize:12.5, marginTop:10, width:'100%', justifyContent:'center'}} onClick={()=>setRoute('calendar')}>
                    Ver calendario completo <Icon.ArrowRight size={12}/>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function SectionHeader({ title, sub, action, onAction, plain }) {
  return (
    <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', padding: plain ? 0 : '18px 22px', borderBottom: plain ? 'none' : '1px solid var(--line-1)'}}>
      <div>
        <div style={{fontSize:14, fontWeight:500, color:'var(--text-1)'}}>{title}</div>
        {sub && <div style={{fontSize:12, color:'var(--text-3)', marginTop:3}}>{sub}</div>}
      </div>
      {action && <button className="btn btn-ghost" style={{fontSize:12.5}} onClick={onAction}>{action} <Icon.ArrowRight size={12}/></button>}
    </div>
  );
}

function FeaturedBid({ bid, onOpen }) {
  if (!bid) return null;
  return (
    <div className="card-feature" style={{padding:32, marginBottom:0, position:'relative'}}>
      <div className="glow-orb" style={{width:340, height:340, background:'var(--primary)', top:-120, right:-60, opacity:0.18}}></div>
      <div className="glow-orb" style={{width:240, height:240, background:'var(--lime)', bottom:-100, left:200, opacity:0.10}}></div>

      <div style={{display:'grid', gridTemplateColumns:'1fr auto', gap:32, alignItems:'flex-start', position:'relative', zIndex:1}}>
        <div style={{minWidth:0}}>
          <div style={{display:'flex', alignItems:'center', gap:10, marginBottom:14}}>
            <span className="sem sem-violet" style={{padding:'4px 12px', fontWeight:500}}>
              <Icon.Star size={11}/> Destacada
            </span>
            <span className="font-mono" style={{fontSize:11.5, color:'var(--text-3)'}}>{bid.code}</span>
            <span style={{fontSize:11.5, color:'var(--text-3)'}}>·</span>
            <span style={{fontSize:11.5, color:'var(--text-3)'}}>{bid.lot}</span>
            <span style={{fontSize:11.5, color:'var(--text-3)'}}>·</span>
            <span style={{fontSize:11.5, color:'var(--text-3)'}}>{bid.entityShort}</span>
          </div>
          <h2 className="font-display-heavy" title={bid.title} style={{fontSize:30, margin:'0 0 16px 0', lineHeight:1.1, letterSpacing:'-0.03em', maxWidth:680}}>
            {bidTitle(bid)}
          </h2>
          <div style={{display:'flex', alignItems:'center', gap:32, flexWrap:'wrap'}}>
            <div>
              <div style={{fontSize:10.5, color:'var(--text-3)', letterSpacing:'0.08em', textTransform:'uppercase', whiteSpace:'nowrap'}}>Presupuesto base</div>
              <div className="font-display-heavy" style={{fontSize:26, marginTop:4, lineHeight:1}}>{fmtMoney(bid.budgetBase)}</div>
            </div>
            <div style={{width:1, height:36, background:'var(--line-2)'}}></div>
            <div>
              <div style={{fontSize:10.5, color:'var(--text-3)', letterSpacing:'0.08em', textTransform:'uppercase', whiteSpace:'nowrap'}}>Coste objetivo</div>
              <div className="font-display-heavy" style={{fontSize:26, marginTop:4, lineHeight:1}}>{bid.estimatedCost ? fmtMoney(bid.estimatedCost) : '—'}</div>
            </div>
            <div style={{width:1, height:36, background:'var(--line-2)'}}></div>
            <div>
              <div style={{fontSize:10.5, color:'var(--text-3)', letterSpacing:'0.08em', textTransform:'uppercase', whiteSpace:'nowrap'}}>Margen</div>
              <div className="font-display-heavy" style={{fontSize:26, marginTop:4, lineHeight:1, color:'var(--sem-success)'}}>{bid.margin != null ? fmtPct(bid.margin) : '—'}</div>
            </div>
            <div style={{width:1, height:36, background:'var(--line-2)'}}></div>
            <div>
              <div style={{fontSize:10.5, color:'var(--text-3)', letterSpacing:'0.08em', textTransform:'uppercase', whiteSpace:'nowrap'}}>Cierra en</div>
              <div className="font-display-heavy" style={{fontSize:26, marginTop:4, lineHeight:1, color:'var(--sem-warning)'}}>{(bid.deadlineDays ?? 0) > 0 ? `${bid.deadlineDays} días` : '—'}</div>
            </div>
          </div>
        </div>

        <div style={{display:'flex', flexDirection:'column', gap:8, flexShrink:0, alignItems:'flex-end'}}>
          <button className="btn btn-primary" onClick={onOpen} style={{padding:'12px 18px', fontSize:13.5}}>
            Ver ficha completa <Icon.ArrowRight size={14}/>
          </button>
          <button className="btn btn-ghost" style={{fontSize:12.5}} onClick={onOpen}>
            <Icon.PenTool size={13}/> Abrir ficha
          </button>
        </div>
      </div>
    </div>
  );
}

function KpiCardV2({ label, value, unit, sub, delta, tone='default', icon }) {
  const Ico = icon ? Icon[icon] : null;
  const className =
    tone === 'accent' ? 'kpi kpi-accent' :
    tone === 'lime'   ? 'kpi kpi-lime' :
    'kpi';
  const deltaTone =
    tone === 'accent' ? { color:'#fff', background:'rgba(255,255,255,0.16)' } :
    tone === 'warning' ? { color:'var(--sem-warning)', background:'var(--sem-warning-bg)' } :
    { color:'var(--sem-success)', background:'var(--sem-success-bg)' };
  return (
    <div className={className}>
      <div style={{display:'flex', alignItems:'center', justifyContent:'space-between'}}>
        <div className="kpi-label">{label}</div>
        {Ico && (
          <div style={{
            width:32, height:32, borderRadius:10,
            background: tone === 'accent' ? 'rgba(255,255,255,0.16)' : 'var(--bg-2)',
            display:'flex', alignItems:'center', justifyContent:'center',
            color: tone === 'accent' ? '#fff' : 'var(--text-2)',
          }}>
            <Ico size={15}/>
          </div>
        )}
      </div>
      <div style={{display:'flex', alignItems:'baseline', gap:8, marginTop:8}}>
        <span className="kpi-value">{value}</span>
        {unit && <span style={{fontSize:14, color: tone === 'accent' ? 'rgba(255,255,255,0.7)':'var(--text-3)'}}>{unit}</span>}
      </div>
      <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', marginTop:'auto', paddingTop:14}}>
        <span style={{fontSize:12.5, color: tone === 'accent' ? 'rgba(255,255,255,0.7)':'var(--text-3)'}}>{sub}</span>
        {delta && <span style={{fontSize:11, padding:'3px 8px', borderRadius:999, fontWeight:500, ...deltaTone}}>{delta}</span>}
      </div>
    </div>
  );
}

function PipelineBar({ segments }) {
  const segs = (segments && segments.length)
    ? segments.map(s => ({ c: `var(--${s.c})`, pct: Math.max(s.n, 0), label: s.l }))
    : [];
  const total = segs.reduce((s, x) => s + x.pct, 0);
  return (
    <div style={{display:'flex', height:10, borderRadius:999, overflow:'hidden', background:'var(--bg-3)'}}>
      {total > 0 && segs.map((s,i) => s.pct > 0 && <div key={i} style={{ flex: s.pct, background: s.c }} title={s.label}></div>)}
    </div>
  );
}

function KpiCard({ label, value, sub, delta, tone='neutral' }) {
  return <KpiCardV2 label={label} value={value} sub={sub} delta={delta} tone={tone === 'success' ? 'accent' : tone}/>;
}

function ActiveBidRow({ bid, onOpen, last }) {
  const lead = window.MockData.USERS.find(u => u.id === bid.teamLead);
  return (
    <div onClick={onOpen}
      style={{
        padding:'16px 22px', display:'grid',
        gridTemplateColumns:'minmax(0, 1fr) 140px 110px 130px 36px',
        gap:14, alignItems:'center', cursor:'pointer',
        borderBottom: last ? '0' : '1px solid var(--line-1)',
        transition:'background 140ms ease',
      }}
      onMouseEnter={(e)=> e.currentTarget.style.background='var(--bg-hover)'}
      onMouseLeave={(e)=> e.currentTarget.style.background='transparent'}>
      <div style={{minWidth:0}}>
        <div style={{display:'flex',alignItems:'center',gap:8, marginBottom:4}}>
          <span className="font-mono" style={{fontSize:10.5, color:'var(--text-4)'}}>{bid.code}</span>
          <span style={{fontSize:10.5, color:'var(--text-4)'}}>·</span>
          <span style={{fontSize:11, color:'var(--text-3)'}}>{bid.entityShort}</span>
        </div>
        <div style={{fontSize:14, color:'var(--text-1)', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', fontWeight:500}} title={bid.title}>{bidTitle(bid)}</div>
      </div>
      <div>
        <StatusChip status={bid.status}/>
      </div>
      <div className="font-mono" style={{fontSize:12.5, color:'var(--text-2)'}}>{fmtMoney(bid.budgetBase)}</div>
      <div>
        <div style={{display:'flex',alignItems:'center',gap:8}}>
          <ProgressBar value={bid.progress}/>
          <span className="font-mono" style={{fontSize:11, color:'var(--text-3)'}}>{bid.progress}%</span>
        </div>
        <div style={{fontSize:11, color: bid.deadlineDays < 15 ? 'var(--sem-warning)' : 'var(--text-4)', marginTop:4}}>
          {bid.deadlineDays > 0 ? `Cierra en ${bid.deadlineDays} d` : 'Cerrada'}
        </div>
      </div>
      <div style={{display:'flex', justifyContent:'flex-end'}}>
        {lead && <Avatar user={lead} size={26}/>}
      </div>
    </div>
  );
}

function CalendarRow({ entry }) {
  const typeMeta = {
    milestone: { icon:'Pin',   tone:'sem-info' },
    meeting:   { icon:'Users', tone:'sem-neutral' },
    deadline:  { icon:'Clock', tone:'sem-warning' },
    submit:    { icon:'Send',  tone:'sem-danger'  },
  }[entry.type];
  const Ico = Icon[typeMeta.icon];
  const date = fmtDate(entry.date).split(' ');
  return (
    <div style={{display:'grid', gridTemplateColumns:'auto 1fr auto', gap:14, alignItems:'center', padding:'12px 4px', borderTop:'1px solid var(--line-1)'}}>
      <div style={{textAlign:'center', minWidth:42, padding:'4px 0'}}>
        <div className="font-display-heavy" style={{fontSize:20, lineHeight:1}}>{date[0]}</div>
        <div style={{fontSize:10, color:'var(--text-3)', textTransform:'uppercase', letterSpacing:'0.06em', marginTop:2}}>{date[1]}</div>
      </div>
      <div style={{minWidth:0}}>
        <div style={{display:'flex',alignItems:'center',gap:8}}>
          <span className={`sem ${typeMeta.tone}`} style={{padding:'2px 6px', fontSize:10}}><Ico size={9}/></span>
          <span style={{fontSize:13.5}}>{entry.title}</span>
        </div>
        <div style={{fontSize:11.5, color:'var(--text-4)', marginTop:3}}>{entry.time} · {window.MockData.BIDS.find(b=>b.id===entry.bid)?.entityShort}</div>
      </div>
      <Avatar user={window.MockData.USERS.find(u=>u.id===entry.owner)} size={22}/>
    </div>
  );
}

window.DashboardScreen = DashboardScreen;
window.KpiCard = KpiCard;
window.KpiCardV2 = KpiCardV2;


export { DashboardScreen, SectionHeader, FeaturedBid, KpiCardV2, PipelineBar, KpiCard, ActiveBidRow, CalendarRow };
